import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Send, Search } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const OverdueNotificationsPanel = () => {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [search, setSearch] = useState("");
  const [candidatos, setCandidatos] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);

  const [enviando, setEnviando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    axios
      .get(`${API}/admin/empresas`, { headers })
      .then((res) => setEmpresas(res.data))
      .catch(() => toast.error("No se pudieron cargar las empresas"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (empresaId) params.empresaId = empresaId;

    const timeout = setTimeout(() => {
      axios
        .get(`${API}/admin/notificaciones/vencidos/preview`, { headers, params })
        .then((res) => setCandidatos(res.data))
        .catch(() => toast.error("No se pudieron cargar los usuarios con vencidos"));
    }, 300);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, empresaId]);

  const cargarHistorial = (p = 1) => {
    axios
      .get(`${API}/admin/notificaciones/vencidos/logs`, { headers, params: { page: p } })
      .then((res) => {
        setHistorial(res.data.data);
        setPage(res.data.page);
        setTotalPages(res.data.totalPages);
      })
      .catch(() => toast.error("No se pudo cargar el historial"));
  };

  useEffect(() => {
    cargarHistorial(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const enviar = async () => {
    if (!empresaId && seleccionados.length === 0) {
      toast.error("Selecciona una empresa o al menos un usuario");
      return;
    }

    setEnviando(true);
    try {
      const body = seleccionados.length
        ? { userIds: seleccionados }
        : { empresaIds: [empresaId] };
      const res = await axios.post(`${API}/admin/notificaciones/vencidos/enviar`, body, {
        headers,
      });
      toast.success(
        `Enviados: ${res.data.enviados} · Fallidos: ${res.data.fallidos} · Elegibles: ${res.data.elegibles}`,
      );
      setSeleccionados([]);
      cargarHistorial(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar");
    } finally {
      setEnviando(false);
    }
  };

  const reactivar = async (usuarioId) => {
    try {
      await axios.patch(
        `${API}/admin/notificaciones/vencidos/usuarios/${usuarioId}/reactivar`,
        {},
        { headers },
      );
      toast.success("Usuario reactivado");
      cargarHistorial(page);
    } catch {
      toast.error("No se pudo reactivar el usuario");
    }
  };

  return (
    <>
      <h1 className="text-xl font-semibold text-gray-800 mb-6">
        Recordatorios de mantenimientos vencidos
      </h1>

      <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4 mb-8">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              placeholder="Buscar por nombre o email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={empresaId}
            onChange={(e) => setEmpresaId(e.target.value)}
          >
            <option value="">Selecciona una empresa</option>
            {empresas.map((emp) => (
              <option key={emp._id} value={emp._id}>
                {emp.razon_social}
              </option>
            ))}
          </select>
        </div>

        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
          {candidatos.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              Sin usuarios con vencidos para este filtro
            </p>
          ) : (
            candidatos.map((u) => (
              <label
                key={u._id}
                className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={seleccionados.includes(u._id)}
                  onChange={() => toggleSeleccionado(u._id)}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{u.name}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-red-100 text-red-700">
                  {u.vencidosCount} vencido(s)
                </span>
              </label>
            ))
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {seleccionados.length > 0
              ? `${seleccionados.length} usuario(s) seleccionado(s)`
              : empresaId
                ? "Se enviará a todos los elegibles de la empresa seleccionada"
                : "Selecciona una empresa o usuarios específicos"}
          </span>
          <button
            onClick={enviar}
            disabled={enviando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg"
          >
            <Send className="h-4 w-4" />
            {enviando ? "Enviando..." : "Enviar ahora"}
          </button>
        </div>
      </div>

      <h2 className="text-lg font-semibold text-gray-800 mb-4">Historial</h2>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-2">Usuario</th>
              <th className="text-left px-4 py-2">Empresa</th>
              <th className="text-left px-4 py-2">Fecha</th>
              <th className="text-left px-4 py-2">Origen</th>
              <th className="text-left px-4 py-2">Cronogramas</th>
              <th className="text-left px-4 py-2">Estado</th>
              <th className="text-left px-4 py-2">Exento</th>
            </tr>
          </thead>
          <tbody>
            {historial.map((log) => (
              <tr key={log._id} className="border-t border-gray-100">
                <td className="px-4 py-2 text-gray-800">
                  {log.usuario?.name || log.email}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {log.empresa?.razon_social || "-"}
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {new Date(log.createdAt).toLocaleString("es-CO")}
                </td>
                <td className="px-4 py-2 text-gray-600">{log.origen}</td>
                <td className="px-4 py-2 text-gray-600">{log.cronogramas.length}</td>
                <td className="px-4 py-2">
                  <span
                    className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      log.estadoEnvio === "enviado"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {log.estadoEnvio}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {log.usuario?.notificacionesVencidosExento ? (
                    <button
                      onClick={() => reactivar(log.usuario._id)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Reactivar
                    </button>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => cargarHistorial(p)}
              className={`px-3 py-1 rounded-lg text-sm ${
                p === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default OverdueNotificationsPanel;
